import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * Address Validation
 *--------------------------------------------------------------------------
 *
 * Class to support native Miva address validation functionality.
 *
 */
export default class AddressValidation {

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Class options object. See class constructor() for option definitions.
	 *
	 * @var Object
	 */
	options;

	/**
	 * Root container element for address validation fields
	 *
	 * @var	Element
	 */
	containerElement;

	/**
	 * List of primary addresses
	 *
	 * @var	NodeList
	 */
	primaryAddressList;

	/**
	 * List of secondary addresses
	 *
	 * @var	NodeList
	 */
	secondaryAddressList;

	/**
	 * Input prefix for the primary input fields
	 *
	 * @var	String
	 */
	primaryPrefix;

	/**
	 * Input prefix for the secondary input fields
	 *
	 * @var	String
	 */
	secondaryPrefix;

	/**
	 * Address validation form element
	 *
	 * @var	Element
	 */
	formElement;

	/**
	 * Flag to determine if secondary fields are displayed (ex - billing to show / shipping to show)
	 *
	 * @var	Boolean
	 */
	secondaryShown;

	/**
	 * List of all addresses (primary address list + secondary address list)
	 *
	 * @var	Array
	 */
	addressList;

	/**
	 * Create a new AddressValidation object
	 *
	 * @param	Element		containerElement					Root container element for address validation fields.
	 * @param	Element		options.formElement					Address validation form element. Defaults to '.js-validate-address'.
	 * @param	ElementList	options.primaryAddressElements		Primary address elements. Defaults to '.js-primary-address'.
	 * @param	String		options.primaryPrefixAttribute		Primary address prefix attribute. Set on the containerElement element. Defaults to 'data-primary-address-prefix'.
	 * @param	ElementList	options.secondaryAddressElements	Secondary address elements. Defaults to '.js-secondary-address'.
	 * @param	String		options.secondaryPrefixAttribute	Secondary address prefix attribute. Set on the containerElement element. Defaults to 'data-secondary-address-prefix'.
	 * @param	Element		options.secondaryShownElement		Element containing the attribute selector to flag if the secondary fields are displayed. Defaults to '[data-secondary-shown]'.
	 *
	 */
	constructor (containerElement, {
		formElement = '.js-validate-address',
		primaryAddressElements = '.js-primary-address',
		primaryPrefixAttribute = 'data-primary-address-prefix',
		secondaryAddressElements = '.js-secondary-address',
		secondaryPrefixAttribute = 'data-secondary-address-prefix',
		secondaryShownElement = '[data-secondary-shown]'
	} = {}) {
		this.#validator = new ParameterValidator(this);

		this.containerElement = this.#validator.isElement('containerElement', containerElement);
		this.formElement = this.#validator.isElement('formElement', formElement);

		this.primaryAddressElements = this.#validator.isElementList('primaryAddressElements', primaryAddressElements, {container: this.containerElement});
		this.primaryPrefix = this.#validator.isDefined('primaryPrefixAttribute', this.containerElement.getAttribute(primaryPrefixAttribute));

		this.secondaryAddressElements = this.#validator.isElementList('secondaryAddressElements', secondaryAddressElements, {container: this.containerElement});
		this.secondaryPrefix = this.#validator.isDefined('secondaryPrefixAttribute', this.containerElement.getAttribute(secondaryPrefixAttribute));
		this.secondaryShown = Boolean(this.#validator.isElement('secondaryShownElement', secondaryShownElement, {
			container: this.containerElement,
			required: false
		}));

		this.setAddressList();
		this.bindEvents();
		this.setFirstSuggestion();
	}

	setAddressList () {
		this.addressList = Array.prototype.slice.call(this.primaryAddressElements);

		if (this.secondaryAddressElements.length) {
			this.addressList = this.addressList.concat(Array.prototype.slice.call(this.secondaryAddressElements));
		}
	}

	bindEvents () {
		this.bindAddressListEvents();
	}

	bindAddressListEvents () {
		for (const address of this.addressList) {
			address.addEventListener('change', this.copyFields.bind(this));
		}
	}

	setFirstSuggestion () {
		if (this.primaryAddressElements.length) {
			this.primaryAddressElements[0].checked = true;
		}

		if (this.secondaryShown && this.secondaryAddressElements.length) {
			this.secondaryAddressElements[0].checked = true;
		}

		this.copyFields();
	}

	copyFields () {
		const primarySelectedAddress = this.#validator.isElement('primarySelectedAddress', this.getSelectedAddress());

		this.setFormFields(this.primaryPrefix, primarySelectedAddress);

		if (this.secondaryShown && this.secondaryAddressElements.length) {
			const secondarySelectedAddress = this.#validator.isElement('secondarySelectedAddress', this.getSelectedAddress('secondary'));

			this.setFormFields(this.secondaryPrefix, secondarySelectedAddress);
		}
	}

	getSelectedAddress (addressType = 'primary') {
		const typeVal = this.#validator.isString('addressType', addressType).toLowerCase();

		this.#validator.inSet('typeVal', typeVal, [
			'primary',
			'secondary'
		]);

		const addressList = typeVal === 'secondary' ? this.secondaryAddressElements : this.primaryAddressElements;

		return Array.from(addressList).find((address) => {
			return address.checked;
		});
	}

	setFormFields (prefix, addessInput) {
		if (this.formElement.elements[prefix + 'Address1']) {
			this.formElement.elements[prefix + 'Address1'].value = addessInput.hasAttribute('data-mm_address_addr1') ? addessInput.getAttribute('data-mm_address_addr1') : '';
		}

		if (this.formElement.elements[prefix + 'Address2']) {
			this.formElement.elements[prefix + 'Address2'].value = addessInput.hasAttribute('data-mm_address_addr2') ? addessInput.getAttribute('data-mm_address_addr2') : '';
		}

		if (this.formElement.elements[prefix + 'City']) {
			this.formElement.elements[prefix + 'City'].value = addessInput.hasAttribute('data-mm_address_city') ? addessInput.getAttribute('data-mm_address_city') : '';
		}

		if (this.formElement.elements[prefix + 'Company']) {
			this.formElement.elements[prefix + 'Company'].value = addessInput.hasAttribute('data-mm_address_comp') ? addessInput.getAttribute('data-mm_address_comp') : '';
		}

		if (this.formElement.elements[prefix + 'Country']) {
			this.formElement.elements[prefix + 'Country'].value = addessInput.hasAttribute('data-mm_address_country') ? addessInput.getAttribute('data-mm_address_country') : '';
		}

		if (this.formElement.elements[prefix + 'State']) {
			this.formElement.elements[prefix + 'State'].value = addessInput.hasAttribute('data-mm_address_state') ? addessInput.getAttribute('data-mm_address_state') : '';
		}

		if (this.formElement.elements[prefix + 'StateSelect']) {
			this.formElement.elements[prefix + 'StateSelect'].value = addessInput.hasAttribute('data-mm_address_state') ? addessInput.getAttribute('data-mm_address_state') : '';
		}

		if (this.formElement.elements[prefix + 'Zip']) {
			this.formElement.elements[prefix + 'Zip'].value = addessInput.hasAttribute('data-mm_address_zip') ? addessInput.getAttribute('data-mm_address_zip') : '';
		}
	}
}
